import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "@storytell/markdown";
import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent, subscribeToBlockDeltas } from "@storytell/streaming";
import { TbChevronRight, TbFileText } from "solid-icons/tb";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { defaultMarkdownOptions } from "../block/markdownOptions";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { LoadingDots } from "../ui";

interface SummaryData {
  type: "summary";
  content: string;
}

interface SummaryRendererProps {
  block: StreamingBlock;
}

/**
 * Unwraps the summary tool's JSON envelope. A block that streamed carries plain
 * markdown — the backend's content extractor already unwrapped it — and is only
 * a summary at all because the emitter titled it; anything else is not ours to
 * render.
 */
export const summaryContent = (raw: string, blockTitle: string | undefined): string => {
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw) as Partial<SummaryData> | null;
    if (parsed && typeof parsed === "object" && parsed.type === "summary") return parsed.content ?? "";
  } catch {
    // Not JSON at all.
  }
  // Markdown that happens to parse as JSON — a bare number, `true` — is still markdown.
  return blockTitle === "Summary" ? raw : "";
};

export const SummaryRenderer = (props: SummaryRendererProps) => {
  const rt = useChatRuntime();
  const [isExpanded, setIsExpanded] = createSignal(false);
  const [hasContent, setHasContent] = createSignal(false);

  const markdownStreamer = createSolidStreamingMarkdown();

  const append = (text: string) => {
    if (!text) return;
    markdownStreamer.append(text);
    setHasContent(true);
  };

  onMount(() => {
    const block = props.block;

    if (block.isComplete) {
      const raw =
        block.finalContent?.type === "text" && block.finalContent.text
          ? block.finalContent.text
          : getBlockContent(block.blockId);
      append(summaryContent(raw, props.block.title));
      return;
    }

    // The envelope only ever arrives whole, in the single delta of a tool result
    // that never streamed, so unwrapping is decided once rather than re-parsed
    // over the whole accumulation per delta. Replay hands back the first delta
    // on its own, so a late subscriber still sees that one delta first.
    let isFirstDelta = true;
    onCleanup(
      subscribeToBlockDeltas(
        block.blockId,
        (delta: string) => {
          append(isFirstDelta ? summaryContent(delta, props.block.title) : delta);
          isFirstDelta = false;
        },
        { replay: true }
      )
    );
  });

  const isStreaming = () => !props.block.isComplete;

  return (
    <Show when={hasContent()}>
      <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded())}
          class="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-800"
        >
          <TbChevronRight
            size={14}
            class="shrink-0 text-muted-text transition-transform duration-100"
            classList={{ "rotate-90": isExpanded() }}
          />
          <div
            class="flex size-6 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white dark:bg-violet-500"
            classList={{ "animate-pulse": isStreaming() }}
            style={{
              contain: isStreaming() ? "layout style" : undefined,
              "will-change": isStreaming() ? "opacity" : undefined,
            }}
          >
            <TbFileText class="size-3.5" />
          </div>
          <span class="text-sm font-medium text-primary-text">{rt.t("entity.summary")}</span>
          <Show when={isStreaming()}>
            <LoadingDots />
          </Show>
        </button>
        <Show when={isExpanded() && hasContent()}>
          <div class="border-t border-gray-200 px-3 pb-3 dark:border-slate-700">
            <div class="max-w-none pt-3 text-secondary-text [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mb-1.5 [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-medium [&_h4]:mb-1 [&_h4]:text-sm [&_h4]:font-medium [&_li]:text-sm [&_ol]:my-2 [&_p]:mb-2 [&_p]:text-sm [&_ul]:my-2">
              <SolidStreamingMarkdown doc={markdownStreamer.doc} options={defaultMarkdownOptions} />
            </div>
          </div>
        </Show>
      </div>
    </Show>
  );
};
